import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getStats, getContentSync } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Salad, BookOpen, Leaf, Puzzle, Lightbulb, Plus, Users, RefreshCw, Mail, BarChart3, Activity } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, count, icon: Icon, lastUpdated, color = 'text-[#1A6B3C]' }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{count}</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
            </p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-gray-100 ${color}`}>
          <Icon size={24} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [recentItems, setRecentItems] = useState([]);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, syncRes, userStatsRes] = await Promise.all([
          getStats(),
          getContentSync(),
          axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`)
        ]);
        setStats(statsRes.data);
        setUserStats(userStatsRes.data);
        setRecentRegistrations(userStatsRes.data.recentRegistrations || []);

        // Get recent items from all collections
        const allItems = [
          ...syncRes.data.foods.map(f => ({ ...f, type: 'Food', emoji: f.emoji })),
          ...syncRes.data.recipes.map(r => ({ ...r, type: 'Recipe', emoji: r.emoji })),
          ...syncRes.data.rescueActions.map(a => ({ ...a, type: 'Action', emoji: a.icon })),
          ...syncRes.data.smartCombos.map(c => ({ ...c, type: 'Combo', emoji: c.emoji })),
          ...syncRes.data.dailyTips.map(t => ({ ...t, type: 'Tip', emoji: '💡' })),
        ];

        // Sort by updatedAt and take last 10
        const sorted = allItems
          .filter(item => item.updatedAt)
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
          .slice(0, 10);

        setRecentItems(sorted);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-[100px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Content Stats Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Content Management</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard 
            title="Total Foods" 
            count={stats?.foods?.total || 0} 
            icon={Salad}
            lastUpdated={stats?.foods?.lastUpdated}
          />
          <StatCard 
            title="Total Recipes" 
            count={stats?.recipes?.total || 0} 
            icon={BookOpen}
            lastUpdated={stats?.recipes?.lastUpdated}
          />
          <StatCard 
            title="Rescue Actions" 
            count={stats?.rescueActions?.total || 0} 
            icon={Leaf}
          />
          <StatCard 
            title="Smart Combos" 
            count={stats?.smartCombos?.total || 0} 
            icon={Puzzle}
          />
          <StatCard 
            title="Daily Tips" 
            count={stats?.dailyTips?.total || 0} 
            icon={Lightbulb}
          />
        </div>
      </div>

      {/* User Stats Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">User Management</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard 
            title="Registered Users" 
            count={userStats?.registeredUsers || 0} 
            icon={Users}
            color="text-blue-600"
          />
          <StatCard 
            title="Cloud Syncing" 
            count={userStats?.cloudSyncingUsers || 0} 
            icon={RefreshCw}
            color="text-purple-600"
          />
          <StatCard 
            title="Waitlist" 
            count={userStats?.waitlistCount || 0} 
            icon={Mail}
            color="text-amber-600"
          />
          <StatCard 
            title="Glucose Readings" 
            count={userStats?.glucoseReadingsCount || 0} 
            icon={BarChart3}
            color="text-emerald-600"
          />
          <StatCard 
            title="Medication Logs" 
            count={userStats?.medicationLogsCount || 0} 
            icon={Activity}
            color="text-rose-600"
          />
        </div>
      </div>

      {/* Recent Registrations */}
      {recentRegistrations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={20} />
              Recent User Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentRegistrations.map((reg, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                  data-testid={`recent-reg-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users size={14} className="text-blue-600" />
                    </div>
                    <span className="font-mono text-sm">{reg.maskedEmail}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {reg.createdAt ? formatDistanceToNow(new Date(reg.createdAt), { addSuffix: true }) : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button 
            onClick={() => navigate('/admin/foods', { state: { openNew: true } })}
            className="bg-[#1A6B3C] hover:bg-[#155a32]"
            data-testid="quick-add-food"
          >
            <Plus size={16} className="mr-1" /> New Food
          </Button>
          <Button 
            onClick={() => navigate('/admin/recipes', { state: { openNew: true } })}
            className="bg-[#1A6B3C] hover:bg-[#155a32]"
            data-testid="quick-add-recipe"
          >
            <Plus size={16} className="mr-1" /> New Recipe
          </Button>
          <Button 
            onClick={() => navigate('/admin/tips', { state: { openNew: true } })}
            className="bg-[#1A6B3C] hover:bg-[#155a32]"
            data-testid="quick-add-tip"
          >
            <Plus size={16} className="mr-1" /> New Tip
          </Button>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentItems.length === 0 ? (
            <p className="text-gray-500 text-sm">No recent activity</p>
          ) : (
            <div className="space-y-2">
              {recentItems.map((item, index) => (
                <div 
                  key={`${item.type}-${item.id || index}`}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{item.emoji || '📄'}</span>
                    <div>
                      <p className="font-medium text-sm">{item.name || item.title || item.text?.slice(0, 40) + '...'}</p>
                      <p className="text-xs text-gray-500">{item.type}</p>
                    </div>
                  </div>
                  {item.updatedAt && (
                    <span className="text-xs text-gray-400">
                      {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
